import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Layers, ListChecks } from 'lucide-react';
import { post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { humanize, money } from '@/lib/utils';
import { formatMoneyInput, minorFromInput } from '@/components/MoneyInput';
import { FormActions, FormScreen, FormSection } from '@/components/ui/form-screen';
import { RecordState } from '@/components/ui/record-state';
import { Alert, Checkbox, Field, Input } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { safeReturnTo } from '../opd/BookAppointmentPage';
import { getPages } from './pagedList';
import type { LabPanelRow, LabTestRow } from './LabCataloguePage';

export function LabPanelPage() {
  const { panelId } = useParams<{ panelId: string }>();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get('returnTo'), '/lab/catalogue?tab=panels');

  const tests = useQuery({
    queryKey: ['lab-catalogue-tests'],
    queryFn: () => getPages<LabTestRow>('/lab/tests', { includeInactive: true, limit: 500 }, 4),
  });
  const panels = useQuery({
    queryKey: ['lab-panels'],
    queryFn: () => getPages<LabPanelRow>('/lab/panels', { includeInactive: true, limit: 200 }),
    enabled: Boolean(panelId),
  });

  const panel = panelId ? (panels.data?.find((row) => row.id === panelId) ?? null) : null;
  const loading = tests.isLoading || (Boolean(panelId) && panels.isLoading);

  if (loading || (panelId && !panel)) {
    return (
      <FormScreen title={tr('lab', 'test_catalogue')} icon={Layers} backTo={returnTo}>
        <RecordState error={tests.error ?? panels.error} missing={!loading} label={tr('lab', 'loading')} />
      </FormScreen>
    );
  }

  return <PanelForm key={panel?.id ?? 'new'} panel={panel} tests={tests.data ?? []} returnTo={returnTo} />;
}

function PanelForm({ panel, tests, returnTo }: { panel: LabPanelRow | null; tests: LabTestRow[]; returnTo: string }) {
  const toast = useToast();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [initial] = useState(() => ({
    code: panel?.code ?? '',
    name: panel?.name ?? '',
    price: formatMoneyInput(panel?.basePriceMinor ?? 0),
    isActive: panel?.isActive ?? true,
    testIds: panel?.tests.map((t) => t.test.id) ?? [],
  }));
  const [code, setCode] = useState(initial.code);
  const [name, setName] = useState(initial.name);
  const [price, setPrice] = useState(initial.price);
  const [isActive, setIsActive] = useState(initial.isActive);
  const [testIds, setTestIds] = useState<string[]>(initial.testIds);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () =>
      post('/lab/panels', {
        ...(panel ? { id: panel.id } : {}),
        code: code.trim().toUpperCase(),
        name: name.trim(),
        testIds,
        basePriceMinor: minorFromInput(price),
        isActive,
      }),
    onSuccess: () => {
      toast.success(panel ? tr('lab', 'panel_updated') : tr('lab', 'panel_added'));
      void queryClient.invalidateQueries({ queryKey: ['lab-panels'] });
      navigate(returnTo, { replace: true });
    },
    onError: (caught) => setError(describeError(caught)),
  });

  const needle = search.trim().toLowerCase();
  const active = tests.filter((t) => t.isActive || testIds.includes(t.id));
  const shown = active.filter((t) => !needle || t.name.toLowerCase().includes(needle) || t.code.toLowerCase().includes(needle));
  const chosenTotal = active.filter((t) => testIds.includes(t.id)).reduce((sum, t) => sum + t.basePriceMinor, 0);
  const ready = code.trim().length >= 2 && name.trim().length >= 2 && testIds.length > 0;
  const dirty =
    !save.isSuccess &&
    (code !== initial.code ||
      name !== initial.name ||
      price !== initial.price ||
      isActive !== initial.isActive ||
      JSON.stringify([...testIds].sort()) !== JSON.stringify([...initial.testIds].sort()));

  return (
    <FormScreen
      title={panel ? tr('lab', 'edit_x', { name: panel.name }) : tr('lab', 'add_a_panel')}
      description={tr('lab', 'ordered_by_one_name_resulted_test_by_test')}
      eyebrow={tr('lab', 'test_catalogue')}
      icon={Layers}
      backTo={returnTo}
      dirty={dirty}
      onSubmit={(event) => {
        event.preventDefault();
        if (!ready) return;
        setError(null);
        save.mutate();
      }}
    >
      <FormSection title={tr('lab', 'panel_2')} icon={Layers} columns={3}>
        <Field label={tr('lab', 'code')} required>
          <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={tr('lab', 'cbc')} className="font-mono" />
        </Field>
        <Field label={tr('lab', 'name')} required className="sm:col-span-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={tr('lab', 'complete_blood_count')} />
        </Field>
        <Field label={tr('lab', 'panel_price')} hint={chosenTotal > 0 ? tr('lab', 'tests_alone_x', { chosenTotal: money(chosenTotal) }) : undefined}>
          <Input type="text" dir="ltr" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
        </Field>
        <div className="flex items-end sm:col-span-2">
          <Checkbox checked={isActive} onCheckedChange={setIsActive} label={tr('lab', 'active_orderable')} />
        </div>
      </FormSection>

      <FormSection title={tr('lab', 'tests_in_this_panel_x', { length: testIds.length })} icon={ListChecks} columns={1}>
        <Field label={tr('lab', 'tests')} required>
          <Input className="mb-2" placeholder={tr('lab', 'filter_tests')} value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="max-h-80 overflow-y-auto rounded-md border">
            {shown.length === 0 && <p className="px-3 py-2 text-sm text-muted-foreground">{tr('lab', 'no_test_matches')}</p>}
            {shown.map((t) => (
              <label key={t.id} className="flex cursor-pointer items-center justify-between gap-2 border-b px-3 py-1.5 text-sm last:border-b-0 hover:bg-accent/40">
                <span className="flex items-center gap-2">
                  <Checkbox
                    checked={testIds.includes(t.id)}
                    onCheckedChange={(checked) => setTestIds((current) => (checked ? [...current, t.id] : current.filter((id) => id !== t.id)))}
                  />
                  {t.name} <span className="font-mono text-xs text-muted-foreground">{t.code}</span>
                </span>
                <span className="text-xs text-muted-foreground">{humanize(t.departmentGroup)}</span>
              </label>
            ))}
          </div>
        </Field>
      </FormSection>

      {error && <Alert variant="destructive">{error}</Alert>}

      <FormActions submitLabel={panel ? tr('lab', 'save_changes') : tr('lab', 'add_panel')} loading={save.isPending} incomplete={!ready} cancelTo={returnTo} />
    </FormScreen>
  );
}
